import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import { authApi } from '../../services/authApi';
import './AuthForm.css';

export const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    
    if (emailParam) setEmail(decodeURIComponent(emailParam));
    if (tokenParam) setToken(decodeURIComponent(tokenParam));
  }, [searchParams]);

  const validatePasswords = (): boolean => {
    if (newPassword.length < 6) {
      setValidationError('Password must be at least 6 characters long');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!validatePasswords()) return;

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await authApi.resetPassword(email, token, newPassword, confirmPassword);
      
      if (response.success) {
        setIsSuccess(true);
        setMessage('Password reset successful! Redirecting to login...');
        setTimeout(() => navigate('/'), 2000);
      } else {
        setIsSuccess(false);
        setMessage(response.message || 'Failed to reset password');
      }
    } catch (error: any) {
      setIsSuccess(false);
      setMessage(error.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (validationError) setValidationError(null);
    if (message) setMessage(null);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (validationError) setValidationError(null);
    if (message) setMessage(null);
  };

  // Check if we have the required parameters
  if (!email || !token) {
    return (
      <div className="auth-form-container">
        <h2 className="auth-form-title">Reset Password</h2>
        <div className="auth-error-message">
          Invalid or missing reset link. Please request a new password reset.
        </div>
        <div className="auth-switch-container">
          <p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="auth-switch-button"
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form-container">
      <h2 className="auth-form-title">Reset Password</h2>
      
      {(message || validationError) && (
        <div className={isSuccess ? 'auth-success-message' : 'auth-error-message'}>
          {validationError || message}
        </div>
      )}

      {!isSuccess && (
        <>
          <p style={{ color: 'var(--ut-text-secondary)', marginBottom: '20px', fontSize: 'var(--ut-font-body-sm)' }}>
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            <FormGroup>
              <FormLabel htmlFor="email">
                Email
              </FormLabel>
              <FormInput
                type="email"
                id="email"
                value={email}
                disabled
                readOnly
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="newPassword">
                New Password
              </FormLabel>
              <FormInput
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={handlePasswordChange}
                required
                disabled={isSubmitting}
                placeholder="Enter new password"
                minLength={6}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="confirmPassword">
                Confirm Password
              </FormLabel>
              <FormInput
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                disabled={isSubmitting}
                placeholder="Confirm new password"
                minLength={6}
              />
            </FormGroup>

            <FormButton
              type="submit"
              disabled={isSubmitting || !newPassword || !confirmPassword}
              variant="primary"
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </FormButton>
          </form>
        </>
      )}

      <div className="auth-switch-container">
        <p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="auth-switch-button"
            disabled={isSubmitting}
          >
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};
